import React, {useState} from 'react';
import {Link} from "react-router-dom";

import CountryFlagAndName from '../../components/countryFlagAndName.js';
import DomainTable from '../../components/domainTable.js'
import GeoDistribution from '../../components/geoDistribution.js'
import SectionHeader from '../../components/sectionHeader.js';
import TechnologySmallIcon from '../../components/technologySmallIcon.js';
import useGoogleCharts from '../../components/useGoogleCharts.js';
import DistributionByPopularityChart from '../../components/technologyDistributionChart.js'

import {
    _printNumber, 
    _printPercent,
    _countryNameFromCode
} from '../../helpers/helpers'

import {RtResponseHeaderInfoPageResult} from '../../../../server/routes/response-header.js'

interface ResponseHeaderProps {
    server: string;
    pageData: unknown;
}

interface ComponentProps {
    props: RtResponseHeaderInfoPageResult;
}

export default function ResponseHeader({server, pageData}: ResponseHeaderProps) {

	let [data, setData] = useState(pageData as RtResponseHeaderInfoPageResult);

    return (
        <>
            <section className="section section-lg">
                <div className="container">
                    <div className="row row-50 justify-content-md-center">
                        <div className="col-md-8 col-lg-8 col-xl-8">
                             <HeaderLeftPanel props={data}/>
                        </div>
                        <div className="col-md-4 col-lg-4 col-xl-4">
                            <HeaderRightPanel props={data}/>
                        </div>
                    </div>
                    <HeaderBottomPanel props={data}/>
                </div>
            </section>
        </>
    )
}

function HeaderLeftPanel({props}: ComponentProps) {

    return (<>

        <h2 style={{wordWrap: 'break-word'}}>
            {props.Header}
        </h2> HTTP response header
        <p>{props.HeaderDescription}</p>

        <div className="row row-50">
            <div className="col-12">
                <SectionHeader header={props.HeaderUsage.Header} />
                <table className="table-custom table-custom-striped table-custom-primary">
                        <thead>
                            <tr>
                                <th style={{width: '60%'}}></th>
                                <th style={{width: '40%'}}></th>  
                            </tr>
                        </thead>                                
                        <tbody>
                        <tr>
                            <td>Websites using header {props.Header}</td>
                            <td>
                                <span className="icon text-primary">
                                    <Link 
                                        to={`/response-header/${props.Header}/websites`}
                                        className="link-hover" 
                                        title={`List of live websites that are utilizing ${props.Header} header`}
                                    >
                                        <b>{_printNumber(props.HeaderUsage.HeaderCount)}</b>
                                    </Link>
                                </span>
                            </td>
                        </tr>
                        {
                            props.HeaderUsage.UsedByPercent &&
                            <tr>
                                <td>
                                    Percentage of websites that use {props.Header} header
                                </td>
                                <td>
                                    <span className="icon text-primary">
                                        <b>
                                            {_printPercent(props.HeaderUsage.UsedByPercent)}%
                                        </b>
                                    </span>
                                </td>
                            </tr>
                        }
                        {
                            props.HeaderUsage.UniqueValueCount &&
                            <tr>
                                <td>Total discovered header values</td>
                                {
                                    props.HeaderUsage.UniqueHeaderValueCountMoreThan10K ?
                                    <td>
                                        <span className="icon text-primary"><b>More than 10,000</b></span>
                                    </td>:
                                    <td>
                                        <span className="icon text-primary">
                                            <b>
                                                {_printNumber(props.HeaderUsage.UniqueValueCount)}
                                            </b>
                                        </span>
                                    </td>
                                }
                            </tr>
                        }
                        {
                            props.HeaderInfo.UsesDirectives &&
                            <tr>
                                <td>Header uses directives</td>
                                <td><span className="icon text-primary"><b>{props.HeaderInfo.UsesDirectives}</b></span></td>
                            </tr>
                        }
                        {
                            props.HeaderInfo.ValueUniqueOrRandon &&
                            <tr>
                                <td>Header values are unique or random</td>
                                <td><span className="icon text-primary"><b>{props.HeaderInfo.ValueUniqueOrRandon}</b></span></td>
                            </tr>
                        }
                        {
                            props.HeaderUsage.MostPopularCountry &&
                            <tr>
                                <td>Most popular in the country</td>
                                <td>
                                    <b>
                                        <CountryFlagAndName countryCode={props.HeaderUsage.MostPopularCountry} />
                                    </b>
                                </td>
                            </tr>
                        }
                    </tbody>
                </table>
            </div>
        </div>    
    
    </>)
}

function HeaderRightPanel({props}: ComponentProps) {

    if (!props.ShowRightPanel) return null;

    return (<>

        <div className="row row-35 row-lg-50">
            <div className="col-sm-12 col-lg-12">
                {
                    props.Directives &&
                    <article className="box-bordered">
                        <h4>{props.Header}
                            {' '}directives ({props.Directives.Count} total)</h4>
                        <ul className="list-marked">
                            {
                                props.Directives.List.map((directive: string, indexKey: number) =>
                                    <li key={indexKey}>
                                        {directive}
                                    </li>
                                )
                            }
                        </ul>
                    </article>
                }
                {
                    props.UsedTogetherHeaders && props.UsedTogetherHeaders.length > 0 &&
                    <article className="box-bordered">
                        <h4>{props.Header}
                            {' '}Often Used Together:</h4>
                        <ul className="list-marked">
                            {
                                props.UsedTogetherHeaders.map((usedTogether, indexKey) =>
                                    <li key={indexKey}>
                                        <Link 
                                            to={`/response-header/${usedTogether.URL}`}
                                            className="link-hover" 
                                            title={`${usedTogether.Header} response header information`} 
                                        >
                                            {usedTogether.Header}
                                        </Link>
                                    </li>
                                )
                            }
                        </ul>
                    </article>
                }
            </div>
        </div>
    </>)
}

function HeaderBottomPanel({props}: ComponentProps) {
	const google = useGoogleCharts();   

 //   <DistributionByPopularity props={props}/>

    return (<>
        <div className="row row-50">
            <div className="col-12">
                <DirectivesUsage props={props}/>
                <HeaderTechnologies props={props}/>
                <DistributionByPopularityChart name={props.Header + ' header'} distribution={props.DistributionByPopularity.Results} google={google}/>		
                <GeoDistribution geoData={props.HeaderGeoData}  google={google}/>
                <DomainTable domainList={props.HeaderDomains}/>
                <HeaderTopValues props={props} />
            </div>
        </div>
    </>)

}

function DirectivesUsage({props}: ComponentProps) {

    if (!props.DirectivesUsage.Stats) return null;
    
    return (<>

        <div className="row row-50">
            <div className="col-12">
                <SectionHeader header={props.DirectivesUsage.Header}/>
                <div className="table-outer table-rounded table-responsive-lg">
                    <table className="table-custom table-custom-striped table-custom-primary  ">
                        <thead>
                            <tr>
                                <th style={{width: '35%'}}>Directive</th>
                                <th style={{width: '30%'}}>Share</th>  
                                <th style={{width: '15%'}}>Websites count</th>
                                <th style={{width: '20%'}}>Unique Values</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                props.DirectivesUsage.Stats.map((directiveUsage, indexKey) =>
                                    <tr key={indexKey}>
                                        <td>
                                            {directiveUsage.Directive}
                                        </td>
                                        <td>
                                            {_printPercent(directiveUsage.MarketShare)}%
                                        </td>                                            
                                        <td>
                                            {_printNumber(directiveUsage.DomainCount)}
                                        </td>
        
                                        <td>
                                            {_printNumber(directiveUsage.ValueCount)}
                                        </td>
                                    </tr>
                                )
                            }
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
 
    </>)

}

function HeaderTechnologies({props}: ComponentProps) {

    if (!props.HeaderTechnologies.Technologies || props.HeaderTechnologies.Technologies.length == 0) return null;

    return (<>
        <div className="row row-50">
            <div className="col-12">
                <SectionHeader header={props.HeaderTechnologies.Header} />
                <table className="table-custom table-custom-striped table-custom-primary">
                    <tbody>
                        {
                            props.HeaderTechnologies.Technologies.map((headerTechnology, indexKey) =>
                                <tr key={indexKey}>
                                    <td>
                                        <TechnologySmallIcon
                                            technologyIcon={headerTechnology.TechnologyIcon} 
                                            technology={headerTechnology.TechnologyShortName}
                                        /> {' '}
                                        <Link 
                                            to={`/technology/${headerTechnology.TechnologyURL}`}
                                            className="link-hover" 
                                            title={`${headerTechnology.TechnologyShortName} customers list, historic trends, and recent website additions`} 
                                        >
                                            {headerTechnology.TechnologyShortName}
                                        </Link>
                                        , category{' '}
                                        <Link 
                                            to={`/technology-type/${headerTechnology.CategoryURL}`}
                                            className="link-hover" 
                                            title={`${headerTechnology.CategoryShortName} category market share and usage statistic`} 
                                        >
                                            {headerTechnology.CategoryLongName}
                                        </Link>
                                        , total{' '}
                                        <Link 
                                            to={`/technology/${headerTechnology.TechnologyURL}/websites`}
                                            className="link-hover" 
                                            title={`List of websites that are using ${headerTechnology.TechnologyShortName} technology`} 
                                        >{' '}
                                            {_printNumber(headerTechnology.TechDomains)}
                                        </Link>
                                        {' websites'}
                                    </td>
                                </tr>
                            )
                        }
                    </tbody>
                </table>
            </div>
        </div>
    </>)

}

function DistributionByPopularity({props}: ComponentProps) {

    if (!props.DistributionByPopularity) return null

    return (<>

        <div className="row row-50">
            <div className="col-12">
                <SectionHeader header={props.DistributionByPopularity.Header}/>
                <table className="table-custom table-custom-striped table-custom-primary">
                    <tbody>
                        <tr>
                            <td>Top 10k sites</td>
                            <td>
                                <span className="icon text-primary">
                                    {_printNumber(props.DistributionByPopularity.Results.Top10KUsage)} websites
                                </span>
                            </td>
                        </tr>
                        <tr>
                            <td>Top 100k sites</td>
                            <td>
                                <span className="icon text-primary">
                                    {_printNumber(props.DistributionByPopularity.Results.Top100KUsage)} websites
                                </span>
                            </td>
                        </tr>
                        <tr>
                            <td>Top 1m sites</td>
                            <td>
                                <span className="icon text-primary">
                                    {_printNumber(props.DistributionByPopularity.Results.Top1MUsage)} websites
                                </span>
                            </td>
                        </tr>
                        <tr>
                            <td>Top 10m sites</td>
                            <td>
                                <span className="icon text-primary">
                                    {_printNumber(props.DistributionByPopularity.Results.Top10MUsage)} websites
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>   

    </>)
}

function HeaderTopValues({props}: ComponentProps) {

    if (!props.TopValues.Values) return null;

    return (<>
    
        <div className="row row-50">
            <div className="col-12">
                <SectionHeader header={props.TopValues.Header}/>
                <table 
                    className="table-custom table-custom-striped table-custom-primary" 
                    style={{tableLayout: 'fixed', width: '100%'}}
                >
                    <thead>
                        <tr>
                            <th style={{width: '75%'}}>Header value</th>
                            <th style={{width: '25%'}}>Value prevalence</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            props.TopValues.Values.map((topValue, indexKey) =>
                                <tr key={indexKey}>
                                    <td style={{wordWrap: 'break-word'}}>{topValue.Value}</td>
                                    <td style={{wordWrap: 'break-word'}}>{topValue.Percentage}%</td>
                                </tr>
                            )
                        }
                    </tbody>
                </table>
            </div>
        </div>
    </>)
}
